import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { useMediaQuery } from '@mui/system';
import LaunchIcon from '@mui/icons-material/Launch';
import Star from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useListingResource } from '@brightmls/nextgen.lae.flc';
import {
  ResourceField,
  useResourceForm,
} from '../hooks/useResourceForm/useResourceForm';
import { ListingResource, MediaField } from '../../../system/field';
import { MediaResource } from '../../../system/media';
import { validateUrl } from './hooks/useUrlValidation';
import MediaEditDeleteButtons from './MediaEditDeleteButtons';

const TourFields: ResourceField[] = [
  {
    Key: 'MEDIA_SHORT_DESCRIPTION',
    Label: 'Virtual Tour Name',
    Layout: 'single',
    ForceNeverDisabled: true,
    Metadata: {
      Key: 'MEDIA_SHORT_DESCRIPTION',
      Type: 'Edm.String',
    },
  },
  {
    Key: 'MEDIA_URL',
    Label: 'Virtual Tour URL',
    Layout: 'single',
    ForceNeverDisabled: true,
    Metadata: {
      Key: 'MEDIA_URL',
      Type: 'Edm.String',
    },
    validationRules: [
      {
        validate: (value, formValues: MediaResource) => {
          if (!value || !formValues.MEDIA_URL) {
            return;
          }

          const urlValidation = validateUrl(formValues.MEDIA_URL);
          if (!urlValidation.isValid) {
            return { error: urlValidation.error ?? 'Invalid URL' };
          }

          return undefined;
        },
      },
    ],
  },
];

interface TourTileProps {
  file: MediaResource;
  hideDelete?: boolean;
  hideEdit?: boolean;
  hideMove?: boolean;
  id?: number;
  moveTourToFirst: (newValue: string) => void;
  onDeleteTour: (newValue: string) => void;
}

const TourTile: React.FC<TourTileProps> = ({
  file,
  hideDelete,
  hideEdit,
  hideMove,
  moveTourToFirst,
  onDeleteTour: deleteTour,
}: TourTileProps) => {
  const { openResourceForm } = useResourceForm();
  const [isConfirmCancel, setIsConfirmCancel] = useState(false);
  const { items: mediaList, set: setMediaList } =
    useListingResource<MediaResource>(ListingResource.Media);
  const isPrimary = file.MEDIA_DISPLAY_ORDER === 0; // TODO: this logic needs to be legacy-proof
  const theme = useTheme();
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('sm'));

  const onDeleteTour = useCallback(() => {
    setIsConfirmCancel(false);
    deleteTour(file.MED_VW_KEY);
  }, [deleteTour, file, setIsConfirmCancel]);

  const saveHook = useCallback(
    (updatedFile: any) => {
      // Find the media item we're updating
      const itemIndex = mediaList.findIndex(
        (item: any) => item.MED_VW_KEY === file.MED_VW_KEY,
      );

      if (itemIndex === -1) return updatedFile;

      const updatedList = [...mediaList];
      const mediaUrl = updatedFile[MediaField.MEDIA_URL];
      updatedList[itemIndex] = {
        ...updatedFile,
        [MediaField.MEDIA_URL]: mediaUrl,
        [MediaField.MEDIA_URLHD]: mediaUrl,
        [MediaField.MEDIA_URL_FULL]: mediaUrl,
        [MediaField.MEDIA_URL_HI_RES]: mediaUrl,
        [MediaField.MEDIA_URL_MEDIUM]: mediaUrl,
        [MediaField.MEDIA_URL_THUMB]: mediaUrl,
        [MediaField.MEDIA_FILE_NAME]: mediaUrl,
      };
      setMediaList(updatedList);

      return updatedFile;
    },
    [file, mediaList, setMediaList],
  );

  const showEditSidePanel = useCallback(() => {
    const itemIndex = mediaList.findIndex(
      (item: any) => item.MED_VW_KEY === file.MED_VW_KEY,
    );

    openResourceForm(
      {
        fields: TourFields,
        resourceType: ListingResource.Media,
        resourceIndex: itemIndex,
        internalResourceManagement: true,
      },
      {
        title: 'Tour',
      },
      saveHook,
      file,
    );
  }, [file, mediaList, saveHook]);

  const handleSetAsPrimary = useCallback(() => {
    moveTourToFirst(file.MED_VW_KEY);
  }, [file]);

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: 1,
        borderColor: 'grey.400',
        borderRadius: 2,
        overflow: 'hidden',
        px: 3,
        width: 1,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          py: 2,
        }}
      >
        <Link
          sx={{ fontWeight: 'bold', color: 'secondary.medium' }}
          href={file.MEDIA_URL}
          underline="always"
          color={'secondary.medium'}
          target="_blank"
        >
          {file.MEDIA_SHORT_DESCRIPTION}
          <IconButton sx={{ color: 'secondary.medium' }} size="small">
            <LaunchIcon />
          </IconButton>
        </Link>
      </Box>
      {!hideMove && (
        <Button
          className="bmls_btn"
          disabled={isPrimary}
          onClick={handleSetAsPrimary}
          size="small"
          sx={{
            pl: 0,
            height: '36px',
            my: 2,
            color: isPrimary ? 'grey' : 'primary.main',
          }}
        >
          {isPrimary ? <Star /> : <StarBorderIcon />}
          <Typography
            sx={{
              color: isPrimary ? 'grey' : 'primary.main',
            }}
          >
            {isPrimary ? 'Current Primary Tour' : 'Set As Primary Tour'}
          </Typography>
        </Button>
      )}
      <Divider className="bmls_divider" />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <MediaEditDeleteButtons
          props={{
            dataProps: file,
            isConfirmCancel,
            setIsConfirmCancel,
            editCallback: showEditSidePanel,
            deleteCallback: onDeleteTour,
            hideEdit,
            hideDelete,
            isSmallDisplay,
          }}
        />
      </Box>
    </Box>
  );
};

export default TourTile;
